import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, TitleStrategy, withInMemoryScrolling } from '@angular/router';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { authInterceptor } from './core/interceptors/auth-error.interceptor';
import { fetchErrorInterceptor } from './core/interceptors/fetch-error.interceptor';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { TranslationsApiService } from './core/api/translations-api.service';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppTranslateLoader } from './core/loaders/translations-loader';
import { I18nService } from './shared/services/i18n.service';
import { DialogService } from 'primeng/dynamicdialog';
import { PixiePreset } from '../themes/pixie.preset';
import { deviceIdInterceptor } from './core/interceptors/device-id.interceptor';
import { localizationInterceptor } from './core/interceptors/localization.interceptor';
import { guestCartInterceptor } from './core/interceptors/guest-cart.interceptor';
import { LocalizationService } from './shared/services/localization.service';
import { firstValueFrom } from 'rxjs';
import { ScrollService } from './shared/services/scroll.service';
import { PageTitleStrategy } from './core/strategies/page-title.strategy';

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withFetch(),
      withInterceptors([
        authInterceptor,
        fetchErrorInterceptor,
        tokenInterceptor,
        deviceIdInterceptor,
        localizationInterceptor,
        guestCartInterceptor,
      ])
    ),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withInMemoryScrolling({
      scrollPositionRestoration: 'disabled',
      anchorScrolling: 'enabled'
    })),
    provideAnimationsAsync(),
    MessageService,
    DialogService,
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    providePrimeNG({
      theme: {
        preset: PixiePreset,
        options: {
          // Force-disable PrimeNG system dark mode.
          darkModeSelector: false
        }
      }
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: AppTranslateLoader,
          deps: [TranslationsApiService]
        }
      })
    ),
    provideAppInitializer(() => {
      const countryService = inject(LocalizationService);
      return countryService.initialize();
    }),
    provideAppInitializer(() => {
      const scrollService = inject(ScrollService);
      // Initialize scroll service (sets up listener for fragment="top")
      return Promise.resolve();
    }),
    provideAppInitializer(async () => {
      const i18nService = inject(I18nService);
      // Set fallback and load default translations
      i18nService.translateService.setFallbackLang(i18nService.fallbackLang);
      // Load fallback translations so interceptors have something to use
      await firstValueFrom(i18nService.translateService.use(i18nService.fallbackLang));
    })
  ]
};
